'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import { Eye, EyeOff, Loader2, Copy, Check, ArrowRight, Home } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type SignUpFormData = {
  displayName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

type SignInFormData = {
  email: string;
  password: string;
  rememberMe: boolean;
};

const MOCK_CREDENTIALS = [
  { role: 'Creator', email: 'riya.kapoor@nexus.global', password: 'nexus#2026' },
  { role: 'Member', email: 'marcus.osei@nexus.global', password: 'intent#2026' },
];

const INTEREST_TOPICS = [
  { id: 'ai-tools', label: 'AI & Tools' },
  { id: 'ux-design', label: 'UX Design' },
  { id: 'distributed-systems', label: 'Distributed Systems' },
  { id: 'tech-policy', label: 'Tech Policy' },
  { id: 'open-source', label: 'Open Source' },
  { id: 'startups', label: 'Startups' },
  { id: 'data-science', label: 'Data Science' },
  { id: 'security', label: 'Security' },
];

interface AuthFormProps {
  mode: 'signup' | 'signin';
  onModeChange: (mode: 'signup' | 'signin') => void;
}

export default function AuthForm({ mode, onModeChange }: AuthFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [authError, setAuthError] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const signUpForm = useForm<SignUpFormData>({
    mode: 'onBlur',
    defaultValues: { displayName: '', email: '', password: '', confirmPassword: '' },
  });

  const signInForm = useForm<SignInFormData>({
    mode: 'onBlur',
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const handleCredentialFill = (cred: typeof MOCK_CREDENTIALS[0]) => {
    if (mode === 'signin') {
      signInForm.setValue('email', cred.email);
      signInForm.setValue('password', cred.password);
    } else {
      signUpForm.setValue('email', cred.email);
      signUpForm.setValue('password', cred.password);
    }
  };

  const toggleTopic = (id: string) => {
    setSelectedTopics((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleSignUp = async (data: SignUpFormData) => {
    if (data.password !== data.confirmPassword) {
      signUpForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    setAuthError('');
    // Backend integration point: POST /api/auth/signup with { displayName, email, password, topics: selectedTopics }
    await new Promise((r) => setTimeout(r, 1400));
    setIsLoading(false);
    setSuccessMessage('Account created! Check your email to confirm, then sign in.');
  };

  const handleSignIn = async (data: SignInFormData) => {
    setIsLoading(true);
    setAuthError('');
    // Backend integration point: POST /api/auth/signin with { email, password }
    await new Promise((r) => setTimeout(r, 1200));
    const validCred = MOCK_CREDENTIALS.find(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!validCred) {
      setIsLoading(false);
      setAuthError('Invalid credentials — use the demo accounts below to sign in.');
      return;
    }
    setIsLoading(false);
    window.location.href = '/home-feed';
  };

  const switchMode = (newMode: 'signup' | 'signin') => {
    setAuthError('');
    setSuccessMessage('');
    signUpForm.reset();
    signInForm.reset();
    onModeChange(newMode);
  };

  return (
    <div className="w-full">
      {/* Mobile logo */}
      <div className="flex lg:hidden items-center justify-between mb-8">
        <Link href="/" className="flex items-center gap-2">
          <AppLogo size={28} />
          <span className="font-sans text-xl font-800 tracking-widest text-foreground">NEXUS</span>
        </Link>
        <Link href="/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Home size={14} />
          <span>Home</span>
        </Link>
      </div>

      {/* Heading */}
      <div className="mb-8">
        <h2 className="text-2xl font-700 text-foreground mb-2">
          {mode === 'signup' ? 'Create your account' : 'Welcome back'}
        </h2>
        <p className="text-sm text-muted-foreground">
          {mode === 'signup' ?'Join NEXUS and start discovering what matters to you.' :'Sign in to your NEXUS account.'}
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex p-1 rounded-xl bg-muted border border-border mb-6">
        {(['signup', 'signin'] as const).map((m) => (
          <button
            key={`mode-${m}`}
            onClick={() => switchMode(m)}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-600 transition-all duration-150 ${
              mode === m
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {m === 'signup' ? 'Sign Up' : 'Sign In'}
          </button>
        ))}
      </div>

      {/* Success state */}
      {successMessage && (
        <div className="mb-5 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-400">
          {successMessage}
        </div>
      )}

      {/* Auth error */}
      {authError && (
        <div className="mb-5 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">
          {authError}
        </div>
      )}

      {/* SIGN UP FORM */}
      {mode === 'signup' && (
        <form onSubmit={signUpForm.handleSubmit(handleSignUp)} className="space-y-4">
          {/* Display name */}
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">
              Display name <span className="text-red-400">*</span>
            </label>
            <input
              {...signUpForm.register('displayName', {
                required: 'Display name is required',
                minLength: { value: 2, message: 'At least 2 characters' },
                maxLength: { value: 50, message: 'Maximum 50 characters' },
              })}
              className={`input-field ${signUpForm.formState.errors.displayName ? 'error' : ''}`}
              placeholder="Your name on NEXUS"
              autoComplete="name"
            />
            {signUpForm.formState.errors.displayName && (
              <p className="mt-1.5 text-xs text-red-400">{signUpForm.formState.errors.displayName.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">
              Email address <span className="text-red-400">*</span>
            </label>
            <input
              {...signUpForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
              })}
              type="email"
              className={`input-field ${signUpForm.formState.errors.email ? 'error' : ''}`}
              placeholder="you@domain.com"
              autoComplete="email"
            />
            {signUpForm.formState.errors.email && (
              <p className="mt-1.5 text-xs text-red-400">{signUpForm.formState.errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">
              Password <span className="text-red-400">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">At least 8 characters</p>
            <div className="relative">
              <input
                {...signUpForm.register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'At least 8 characters required' },
                })}
                type={showPassword ? 'text' : 'password'}
                className={`input-field pr-11 ${signUpForm.formState.errors.password ? 'error' : ''}`}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signUpForm.formState.errors.password && (
              <p className="mt-1.5 text-xs text-red-400">{signUpForm.formState.errors.password.message}</p>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">
              Confirm password <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <input
                {...signUpForm.register('confirmPassword', { required: 'Please confirm your password' })}
                type={showConfirmPassword ? 'text' : 'password'}
                className={`input-field pr-11 ${signUpForm.formState.errors.confirmPassword ? 'error' : ''}`}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signUpForm.formState.errors.confirmPassword && (
              <p className="mt-1.5 text-xs text-red-400">{signUpForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          {/* Interest selection */}
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">
              Initial interests <span className="text-muted-foreground font-400">(optional)</span>
            </label>
            <p className="text-xs text-muted-foreground mb-2.5">
              Help the IntentGraph get started. You can always change these later.
            </p>
            <div className="flex flex-wrap gap-2">
              {INTEREST_TOPICS.map((topic) => (
                <button
                  key={`topic-${topic.id}`}
                  type="button"
                  onClick={() => toggleTopic(topic.id)}
                  className={`topic-chip ${selectedTopics.includes(topic.id) ? 'active' : ''}`}
                >
                  {topic.label}
                </button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full justify-center py-3 mt-2"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Creating account...
              </>
            ) : (
              <>
                Create account
                <ArrowRight size={16} />
              </>
            )}
          </button>

          {/* Terms */}
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            By creating an account you agree to our{' '}
            <span className="text-accent hover:underline cursor-pointer">Terms of Service</span>
            {' '}and{' '}
            <span className="text-accent hover:underline cursor-pointer">Privacy Policy</span>.
          </p>
        </form>
      )}

      {/* SIGN IN FORM */}
      {mode === 'signin' && (
        <form onSubmit={signInForm.handleSubmit(handleSignIn)} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">
              Email address <span className="text-red-400">*</span>
            </label>
            <input
              {...signInForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
              })}
              type="email"
              className={`input-field ${signInForm.formState.errors.email ? 'error' : ''}`}
              placeholder="you@domain.com"
              autoComplete="email"
            />
            {signInForm.formState.errors.email && (
              <p className="mt-1.5 text-xs text-red-400">{signInForm.formState.errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-500 text-foreground">
                Password <span className="text-red-400">*</span>
              </label>
              <span className="text-xs text-accent hover:underline cursor-pointer">Forgot password?</span>
            </div>
            <div className="relative">
              <input
                {...signInForm.register('password', { required: 'Password is required' })}
                type={showPassword ? 'text' : 'password'}
                className={`input-field pr-11 ${signInForm.formState.errors.password ? 'error' : ''}`}
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signInForm.formState.errors.password && (
              <p className="mt-1.5 text-xs text-red-400">{signInForm.formState.errors.password.message}</p>
            )}
          </div>

          {/* Remember me */}
          <div className="flex items-center gap-2">
            <input
              {...signInForm.register('rememberMe')}
              type="checkbox"
              id="remember-me"
              className="w-4 h-4 rounded border-border bg-input accent-primary cursor-pointer"
            />
            <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer select-none">
              Remember me for 30 days
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full justify-center py-3"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                Sign in to NEXUS
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>
      )}

      {/* Demo credentials */}
      <div className="mt-6 p-4 rounded-xl border border-border bg-muted/40">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Demo Accounts</span>
          <span className="text-xs text-muted-foreground/60">Click to autofill</span>
        </div>
        <div className="space-y-2">
          {MOCK_CREDENTIALS.map((cred) => (
            <div
              key={`cred-${cred.role}`}
              className="flex items-center justify-between p-2.5 rounded-lg border border-border/50 bg-background/60 cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all duration-150"
              onClick={() => handleCredentialFill(cred)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleCredentialFill(cred)}
            >
              <div className="flex items-center gap-2.5">
                <span className="text-xs font-600 text-accent w-14">{cred.role}</span>
                <span className="text-xs text-muted-foreground font-mono truncate max-w-[140px]">{cred.email}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleCopy(cred.email, `${cred.role}-email`); }}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  aria-label="Copy email"
                >
                  {copiedField === `${cred.role}-email` ? (
                    <Check size={12} className="text-emerald-400" />
                  ) : (
                    <Copy size={12} className="text-muted-foreground" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleCopy(cred.password, `${cred.role}-password`); }}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  aria-label="Copy password"
                >
                  {copiedField === `${cred.role}-password` ? (
                    <Check size={12} className="text-emerald-400" />
                  ) : (
                    <Copy size={12} className="text-muted-foreground" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}